import "@/App.css";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import ComingSoon from "@/components/ComingSoon";
import LabPreview from "@/components/LabPreview";
import DashboardLayout from "@/layouts/DashboardLayout";
import SupportChat from "@/components/SupportChat";
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import MemberDashboard from "@/pages/member/MemberDashboard";
import MemberProfile from "@/pages/member/MemberProfile";
import { PrivacyPolicyPage, TermsOfServicePage, AccountDeletionPage, LuckyDrawRulesPage } from "@/pages/LegalPages";
import MemberRewards from "@/pages/member/MemberRewards";
import MemberIndicators from "@/pages/member/MemberIndicators";
import TopMembers from "@/pages/member/TopMembers";
import MemberEA from "@/pages/member/MemberEA";
import MemberCoins from "@/pages/member/MemberCoins";
import MemberLiveLearn from "@/pages/member/MemberLiveLearn";
import AdminCertificates from "@/pages/admin/AdminCertificates";
import MemberCertificates from "@/pages/member/MemberCertificates";
import MemberLuckyDraw from "@/pages/member/MemberLuckyDraw";
import AdminLuckyDraw from "@/pages/admin/AdminLuckyDraw";
import MemberAI from "@/pages/member/MemberAI";
import AdminAI from "@/pages/admin/AdminAI";
import NewsPage from "@/pages/NewsPage";
import MemberFeedback from "@/pages/member/MemberFeedback";
import WriteFeedback from "@/pages/member/WriteFeedback";
import IBWallet from "@/pages/ib/IBWallet";
import IBDashboard from "@/pages/ib/IBDashboard";
import IBClients from "@/pages/ib/IBClients";
import IBIndicatorRequests from "@/pages/ib/IBIndicatorRequests";
import IBEARequests from "@/pages/ib/IBEARequests";
import IBActivity from "@/pages/ib/IBActivity";
import MemberTopics from "@/pages/member/MemberTopics";
import IBTopics from "@/pages/ib/IBTopics";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminMembers from "@/pages/admin/AdminMembers";
import AdminRanks from "@/pages/admin/AdminRanks";
import AdminRewards from "@/pages/admin/AdminRewards";
import AdminIndicators from "@/pages/admin/AdminIndicators";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminIBs from "@/pages/admin/AdminIBs";
import AdminContent from "@/pages/admin/AdminContent";
import AdminNews from "@/pages/admin/AdminNews";
import AdminFeedback from "@/pages/admin/AdminFeedback";
import AdminSignalProviders from "@/pages/admin/AdminSignalProviders";
import MemberSignalProviders from "@/pages/member/MemberSignalProviders";
import AdminDeletionRequests from "@/pages/admin/AdminDeletionRequests";
import AdminEA from "@/pages/admin/AdminEA";
import AdminWallet from "@/pages/admin/AdminWallet";
import AdminCoins from "@/pages/admin/AdminCoins";
import AdminCoinOrders from "@/pages/admin/AdminCoinOrders";
import AdminLocalDepositors from "@/pages/admin/AdminLocalDepositors";
import IBLocalDepositor from "@/pages/ib/IBLocalDepositor";
import { Activity, BrainCircuit, Server, Wallet } from "lucide-react";
import { BookOpen, GraduationCap } from "lucide-react";

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/terms" element={<TermsOfServicePage />} />
          <Route path="/deletion" element={<AccountDeletionPage />} />
          <Route path="/lucky-draw-rules" element={<LuckyDrawRulesPage />} />

          <Route
            path="/member"
            element={
              <ProtectedRoute roles={["member"]}>
                <DashboardLayout role="member" />
              </ProtectedRoute>
            }
          >
            <Route index element={<MemberDashboard />} />
            <Route path="support" element={<SupportChat role="member" fullPage />} />
            <Route path="rewards" element={<MemberRewards />} />
            <Route path="indicators" element={<MemberIndicators />} />
            <Route path="signal-providers" element={<MemberSignalProviders />} />
            <Route path="signal-providers/:id" element={<MemberSignalProviders />} />
            <Route path="news" element={<NewsPage />} />
            <Route path="feedback" element={<MemberFeedback />} />
            <Route path="feedback/new" element={<WriteFeedback />} />
            <Route path="top-members" element={<TopMembers />} />
            <Route path="expert-advisors" element={<MemberEA />} />
            <Route path="coins" element={<MemberCoins />} />
            <Route path="certificates" element={<MemberCertificates />} />
            <Route path="lucky-draw" element={<MemberLuckyDraw />} />
            <Route
              path="vps"
              element={
                <ComingSoon
                  title="PlusMaker VPS"
                  icon={Server}
                  description="A premium trading VPS solution is currently being prepared for PlusMaker members."
                />
              }
            />
            <Route path="live-learn" element={<MemberLiveLearn />} />
            <Route path="topics" element={<MemberTopics />} />
            <Route
              path="trading-journal"
              element={
                <LabPreview
                  testId="lab-preview-trading-journal"
                  icon={BookOpen}
                  headline="Your Trades. Your Journey. Your Growth."
                  paragraphs={[
                    "Every trader has a story behind every position they take. Every winning trade, every losing trade, and every decision made along the way becomes part of a much bigger journey.",
                    "PlusMaker is developing Your Trading Journal to give you a clearer understanding of that journey.",
                    "Soon, you'll be able to record your trades, organize your trading activity, monitor your performance, and explore valuable insights based on your own trading history.",
                    "From tracking your wins and losses to calculating important performance statistics, Your Trading Journal is being designed to help you look beyond individual trades and understand the bigger picture behind your progress.",
                  ]}
                  sections={[{
                    title: "Coming Soon, You'll Be Able To:",
                    items: [
                      "Track your trading performance over time",
                      "Calculate your profit and loss",
                      "Monitor your win rate and trading statistics",
                      "Review your complete trading history",
                      "Record personal notes and lessons from every trade",
                      "Identify patterns and better understand your trading behaviour",
                      "Build a clearer picture of your personal trading journey",
                    ],
                  }]}
                  closing="Because every trade tells a story. Soon, you'll have a place to understand the entire journey behind it."
                />
              }
            />
            <Route
              path="education"
              element={
                <LabPreview
                  testId="lab-preview-education"
                  icon={GraduationCap}
                  headline="Learn. Understand. Trade Smarter."
                  paragraphs={[
                    "Trading is more than simply entering and exiting the market. Understanding why the market moves, how price reacts, and how different technical concepts work can help traders build a stronger foundation over time.",
                    "PlusMaker is preparing a completely free trading education experience designed to make learning more accessible, structured, and easy to explore.",
                    "Whether you are interested in technical analysis, market structure, support and resistance, liquidity, risk management, or other important trading concepts, you'll be able to explore different learning topics directly from one place.",
                    "The experience is being designed to give you more control over your learning journey.",
                  ]}
                  sections={[
                    {
                      title: "How It Will Work",
                      body: "With just a few clicks, you'll be able to choose the trading topic you want to explore and begin learning at your own pace. No complicated learning structure. No unnecessary barriers. Simply choose what you want to understand and start building your knowledge.",
                    },
                    {
                      title: "Coming Soon, You'll Be Able To Explore:",
                      items: [
                        "Technical Analysis",
                        "Market Structure",
                        "Liquidity Concepts",
                        "Entry and Confirmation Concepts",
                        "Risk Management",
                        "Trading Psychology",
                        "And much more",
                      ],
                    },
                  ]}
                  closing="Your trading journey starts with understanding the market. PlusMaker Education is coming soon."
                />
              }
            />
            <Route path="ai" element={<MemberAI />} />
            <Route path="profile" element={<MemberProfile />} />
          </Route>

          <Route
            path="/ib"
            element={
              <ProtectedRoute roles={["ib"]}>
                <DashboardLayout role="ib" />
              </ProtectedRoute>
            }
          >
            <Route index element={<IBDashboard />} />
            <Route path="clients" element={<IBClients />} />
            <Route path="indicator-requests" element={<IBIndicatorRequests />} />
            <Route path="expert-advisor-requests" element={<IBEARequests />} />
            <Route path="activity" element={<IBActivity />} />
            <Route path="topics" element={<IBTopics />} />
            <Route path="news" element={<NewsPage />} />
            <Route path="wallet" element={<IBWallet />} />
            <Route path="local-depositor" element={<IBLocalDepositor />} />
          </Route>

          <Route
            path="/admin"
            element={
              <ProtectedRoute roles={["admin"]}>
                <DashboardLayout role="admin" />
              </ProtectedRoute>
            }
          >
            <Route index element={<AdminDashboard />} />
            <Route path="support" element={<SupportChat role="admin" fullPage />} />
            <Route path="members" element={<AdminMembers />} />
            <Route path="ranks" element={<AdminRanks />} />
            <Route path="ibs" element={<AdminIBs />} />
            <Route path="rewards" element={<AdminRewards />} />
            <Route path="indicators" element={<AdminIndicators />} />
            <Route path="signal-providers" element={<AdminSignalProviders />} />
            <Route path="news" element={<AdminNews />} />
            <Route path="feedback" element={<AdminFeedback />} />
            <Route path="deletion-requests" element={<AdminDeletionRequests />} />
            <Route path="expert-advisors" element={<AdminEA />} />
            <Route path="wallet" element={<AdminWallet />} />
            <Route path="coins" element={<AdminCoins />} />
            <Route path="coin-orders" element={<AdminCoinOrders />} />
            <Route path="local-depositors" element={<AdminLocalDepositors />} />
            <Route path="certificates" element={<AdminCertificates />} />
            <Route path="lucky-draw" element={<AdminLuckyDraw />} />
            <Route path="ai" element={<AdminAI />} />
            <Route path="content" element={<AdminContent />} />
            <Route path="settings" element={<AdminSettings />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster theme="dark" position="top-right" richColors />
    </AuthProvider>
  );
}

export default App;
